/**
 * `?arm=` URL override — the developer/preview mechanism for viewing a
 * specific arm's UX without touching the experiment.
 *
 * Grammar (one reserved query param, {@link ARM_OVERRIDE_PARAM}):
 *
 *   ?arm=classic-ide                          bare arm name
 *   ?arm=mathide:classic-ide                  qualified with an experiment-ID substring
 *   ?arm=mathide:classic-ide,hints:socratic   comma-separated to stack
 *
 * Overrides are write-free (no flag, no event, no exposure) and ephemeral —
 * drop the param and the student's real assignment applies again. Because
 * they record nothing, they are safe to leave enabled in production.
 *
 * The qualified key matches by substring of the experiment ID, so preview
 * links keep working across course runs.
 */

/** The reserved query parameter name. */
export const ARM_OVERRIDE_PARAM = 'arm';

/** One parsed entry of the override param. */
export interface ArmOverrideEntry {
  /**
   * Experiment-ID substring to match, or null for the bare form (which
   * applies to any experiment containing the arm).
   */
  experimentMatch: string | null;
  /** The requested arm name. */
  arm: string;
}

/**
 * Parses the raw value of the `?arm=` query param.
 *
 * @param paramValue - Raw param value, or null/undefined when absent. When
 *   omitted or empty, returns an empty list (no overrides).
 * @return Parsed entries in order; malformed entries (empty arm) are dropped.
 */
export function parseArmOverrides(
  paramValue: string | null | undefined,
): ArmOverrideEntry[] {
  if (!paramValue) {
    return [];
  }
  const entries: ArmOverrideEntry[] = [];
  for (const rawEntry of paramValue.split(',')) {
    const entry = rawEntry.trim();
    if (entry === '') {
      continue;
    }
    const colonIndex = entry.indexOf(':');
    if (colonIndex === -1) {
      entries.push({experimentMatch: null, arm: entry});
      continue;
    }
    const experimentMatch = entry.slice(0, colonIndex).trim();
    const arm = entry.slice(colonIndex + 1).trim();
    if (arm === '') {
      continue;
    }
    entries.push({
      experimentMatch: experimentMatch === '' ? null : experimentMatch,
      arm,
    });
  }
  return entries;
}

/**
 * Resolves the override (if any) that applies to one experiment.
 *
 * Qualified entries win over bare entries; among equally-qualified entries
 * the first match wins. An entry only applies when its arm is one of the
 * experiment's declared arms — a typo'd arm silently falls through to the
 * student's real assignment.
 *
 * @param options.entries - Parsed entries from {@link parseArmOverrides}.
 * @param options.experimentId - The experiment being resolved.
 * @param options.arms - The experiment's declared arms.
 * @return The overriding arm name, or null when no entry applies.
 */
export function resolveArmOverride({
  entries,
  experimentId,
  arms,
}: {
  entries: readonly ArmOverrideEntry[];
  experimentId: string;
  arms: readonly string[];
}): string | null {
  let bareMatch: string | null = null;
  for (const entry of entries) {
    if (!arms.includes(entry.arm)) {
      continue;
    }
    if (entry.experimentMatch === null) {
      bareMatch = bareMatch ?? entry.arm;
      continue;
    }
    if (experimentId.includes(entry.experimentMatch)) {
      return entry.arm;
    }
  }
  return bareMatch;
}

/**
 * Builds the query-string fragment that forces one arm of one experiment —
 * the qualified form, as used in dashboard preview links.
 *
 * @param options.experimentId - Experiment to target.
 * @param options.arm - Arm to force.
 * @return A `key=value` string (unencoded key, encoded value) suitable for
 *   appending to a URL query.
 */
export function buildArmOverrideParam({
  experimentId,
  arm,
}: {
  experimentId: string;
  arm: string;
}): string {
  return `${ARM_OVERRIDE_PARAM}=${encodeURIComponent(`${experimentId}:${arm}`)}`;
}
